<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AddIcon, ArrowUpIcon, RefreshIcon, StopIcon } from '@solar-icons/vue/linear'
import AppButton from '../../primitives/AppButton/AppButton.vue'
import AppParticleRing from '../../primitives/AppParticleRing/AppParticleRing.vue'
import AppKbd from '../../primitives/AppKbd/AppKbd.vue'
import {
  FIELD_TEXT,
  NEUTRAL_KEY,
  NEUTRAL_KEY_EDGE,
  NEUTRAL_KEY_PRESS,
  PRIMARY_KEY,
  PRIMARY_KEY_SHADOW,
} from '../../primitives/control-surface'
import { vPointerLight } from '../../primitives/v-pointer-light'
import { useTextareaAutosize } from '../../../composables/useTextareaAutosize'
import { useRotatingText } from '../../../composables/useRotatingText'
import { useReducedMotion } from '../../../composables/useReducedMotion'
import { useShake } from '../../../composables/useShake'
import {
  SEND_GLYPH,
  faceSubmits,
  fileMatchesAccept,
  sendFace,
  type PromptStatus,
  type SendFace,
} from './prompt-send'

const props = withDefaults(
  defineProps<{
    modelValue: string
    placeholder?: string
    /** Rotating example prompts shown while the field is empty; falls back to the static `placeholder` when omitted. */
    placeholders?: string[]
    status?: PromptStatus
    disabled?: boolean
    /** Shown in place of the textarea when `disabled`, explaining why (e.g. a closed thread). */
    disabledMessage?: string
    /** Initial visible row count before autosize grows it. */
    minRows?: number
    /** Ceiling for autosize growth; beyond it the textarea scrolls its own content. */
    maxRows?: number
    /** Show the leading attach button and accept dropped files. On by default; the chat surfaces turn it off. */
    attach?: boolean
    accept?: string
    /** Blocks sending without touching the textarea or the attach button - an upload still running. */
    sendDisabled?: boolean
    /** An image-only message (no text) is still sendable when this is true. */
    hasAttachments?: boolean
    /** A context row sits in the `context` slot: the field takes its grown shape without an empty message becoming sendable. */
    hasContext?: boolean
    /** Show the keyboard hint line under the composer on a device with a pointer. */
    keyHint?: boolean
    /** After an error, the failed turn can be sent again: an empty field offers a retry. */
    retryable?: boolean
    /** Draws the drifting blue halo round the field; focus hands it over to the focus ring. */
    halo?: boolean
  }>(),
  {
    placeholder: undefined,
    placeholders: undefined,
    status: 'ready',
    disabled: false,
    disabledMessage: undefined,
    minRows: 1,
    maxRows: 8,
    attach: true,
    accept: 'image/*',
    sendDisabled: false,
    hasAttachments: false,
    hasContext: false,
    keyHint: false,
    retryable: false,
    halo: false,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  submit: [text: string]
  stop: []
  retry: []
  attach: [files: File[]]
}>()

const { t } = useI18n()
const reducedMotion = useReducedMotion()
const resolvedPlaceholder = computed(() => props.placeholder ?? t('chat.composer.placeholder'))

const textareaRef = ref<HTMLTextAreaElement | null>(null)

const { resize } = useTextareaAutosize(textareaRef, { maxRows: props.maxRows })

const isMultiline = ref(false)

const isGrown = computed(() => isMultiline.value || props.hasAttachments || props.hasContext)

let observer: ResizeObserver | null = null

function remeasure(): void {
  isMultiline.value = resize() > 1
}

function onInput(event: Event): void {
  emit('update:modelValue', (event.target as HTMLTextAreaElement).value)
  remeasure()
}

watch(() => props.modelValue, remeasure)

onMounted(() => {
  remeasure()
  const ta = textareaRef.value
  if (ta) {
    let lastWidth = ta.clientWidth
    observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? lastWidth
      if (Math.abs(width - lastWidth) < 1) return
      lastWidth = width
      remeasure()
    })
    observer.observe(ta)
  }
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})

const hasContent = computed(() => props.modelValue.trim() !== '' || props.hasAttachments)

const face = computed<SendFace>(() =>
  sendFace({
    status: props.status,
    hasContent: hasContent.value,
    blocked: props.disabled || props.sendDisabled,
    retryable: props.retryable,
  }),
)

const glyph = computed(() => SEND_GLYPH[face.value])

const sendLabel = computed(() => {
  switch (face.value) {
    case 'sending':
      return t('chat.agentPulse')
    case 'stopping':
      return t('chat.composer.stop')
    case 'retry':
      return t('chat.composer.retry')
    default:
      return t('chat.composer.send')
  }
})

const SEND_BASE =
  'prompt-send control-press press-small relative grid size-(--control-height-md) place-items-center rounded-full select-none outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-blue'

const FACE_SURFACE: Readonly<Record<SendFace, string>> = {
  idle: 'bg-bg-tertiary text-text-tertiary inset-ring-1 inset-ring-transparent cursor-not-allowed',
  ready: `${PRIMARY_KEY} ${PRIMARY_KEY_SHADOW} cursor-pointer`,
  sending: 'bg-bg-secondary text-text-primary inset-ring-1 inset-ring-transparent cursor-progress',
  stopping: `${NEUTRAL_KEY} ${NEUTRAL_KEY_EDGE} ${NEUTRAL_KEY_PRESS} pointer-glow shadow-contact cursor-pointer`,
  retry:
    'bg-halo-red text-accent-red inset-ring-1 inset-ring-accent-red/30 hovered:bg-accent-red/20 pressed:inset-shadow-well cursor-pointer',
}

const popping = ref(false)

watch(face, (next, previous) => {
  if (next === 'ready' && previous !== 'ready' && !reducedMotion.value) popping.value = true
})

function onSendAnimationEnd(event: AnimationEvent): void {
  if (event.animationName === 'prompt-send-pop') popping.value = false
}

function onSendClick(): void {
  if (face.value === 'stopping') emit('stop')
  else if (face.value === 'retry') emit('retry')
}

const nudge = useShake(() => true)

const liftText = ref<string | null>(null)

watch(
  () => props.modelValue,
  (value) => {
    if (value !== '') liftText.value = null
  },
)

function onSubmit(): void {
  if (props.disabled) return
  if (face.value === 'retry') {
    emit('retry')
    return
  }
  const text = props.modelValue.trim()
  if ((!text && !props.hasAttachments) || props.sendDisabled) {
    nudge.shakeIfInvalid()
    return
  }
  if (text && !reducedMotion.value) liftText.value = props.modelValue
  emit('submit', text)
}

const accepting = ref(false)

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Tab' && !event.shiftKey && rotationActive.value && !props.disabled) {
    event.preventDefault()
    emit('update:modelValue', rotatingText.value)
    accepting.value = true
    remeasure()
    return
  }
  if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return
  event.preventDefault()
  onSubmit()
}

function onTextareaAnimationEnd(event: AnimationEvent): void {
  if (event.animationName === 'prompt-accept') accepting.value = false
}

const isBlocked = computed(() => props.disabled && !!props.disabledMessage)

const hasRotatingPlaceholders = computed(
  () => !!props.placeholders && props.placeholders.length > 0,
)

const isFocused = ref(false)

const rotatingLines = toRef(() => props.placeholders ?? [])
const rotationActive = computed(() => hasRotatingPlaceholders.value && props.modelValue === '')
const showTabHint = computed(() => rotationActive.value && isFocused.value)
const { text: rotatingText } = useRotatingText(rotatingLines, rotationActive)

function onFocus(): void {
  isFocused.value = true
}

function onBlur(): void {
  isFocused.value = false
}

function focus(): void {
  textareaRef.value?.focus()
}

const fileInputRef = ref<HTMLInputElement | null>(null)

function openAttachPicker(): void {
  fileInputRef.value?.click()
}

function onFilesChosen(event: Event): void {
  const input = event.target as HTMLInputElement
  const files = Array.from(input.files ?? [])
  if (files.length) emit('attach', files)
  input.value = ''
}

/** A pasted image attaches like a picked one; pasted text is left alone so other paste listeners still see it. */
function onPaste(event: ClipboardEvent): void {
  if (!props.attach) return
  const files = Array.from(event.clipboardData?.files ?? []).filter((file) =>
    file.type.startsWith('image/'),
  )
  if (files.length) emit('attach', files)
}

const acceptsDrop = computed(() => props.attach && !props.disabled)
const dragging = ref(false)
let dragDepth = 0

function carriesFiles(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files')
}

function onDragEnter(event: DragEvent): void {
  if (!acceptsDrop.value || !carriesFiles(event)) return
  event.preventDefault()
  dragDepth += 1
  dragging.value = true
}

function onDragOver(event: DragEvent): void {
  if (!acceptsDrop.value || !carriesFiles(event)) return
  event.preventDefault()
}

function onDragLeave(): void {
  if (!dragging.value) return
  dragDepth = Math.max(0, dragDepth - 1)
  if (dragDepth === 0) dragging.value = false
}

function onDrop(event: DragEvent): void {
  if (!acceptsDrop.value || !carriesFiles(event)) return
  event.preventDefault()
  dragDepth = 0
  dragging.value = false
  const files = Array.from(event.dataTransfer?.files ?? []).filter((file) =>
    fileMatchesAccept(file, props.accept),
  )
  if (files.length) emit('attach', files)
}

defineExpose({ focus, textarea: textareaRef })
</script>

<template>
  <form
    :class="['relative', dragging ? 'prompt-dragging' : '']"
    @submit.prevent="onSubmit"
    @dragenter="onDragEnter"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <div :class="['flex gap-4', isGrown ? 'items-end' : 'items-center']">
      <template v-if="attach && !isBlocked">
        <AppButton
          size="sm"
          class="prompt-plus"
          icon-only
          variant="ghost"
          :start-icon="AddIcon"
          :aria-label="t('chat.composer.attach')"
          :title="t('chat.composer.attach')"
          :is-inactive="disabled"
          @click="openAttachPicker"
        />
        <input
          ref="fileInputRef"
          type="file"
          class="sr-only"
          :accept="accept"
          multiple
          @change="onFilesChosen"
        />
      </template>

      <div class="composer-halo-host relative isolate min-w-0 flex-1" :data-grown="isGrown">
        <template v-if="halo && !isBlocked">
          <span class="composer-halo" data-layer="drift" aria-hidden="true" />
          <span class="composer-halo" data-layer="counter" aria-hidden="true" />
        </template>
        <div
          :class="[
            'prompt-field relative overflow-hidden bg-bg-primary px-8 py-6',
            'inset-shadow-glass-edge shadow-1',
            'focus-within:inset-ring-1 focus-within:inset-ring-accent-blue focus-within:ring-4 focus-within:ring-halo-blue',
          ]"
          :data-grown="isGrown"
        >
          <p v-if="isBlocked" class="text-body text-text-tertiary">{{ disabledMessage }}</p>
          <div v-else class="flex flex-col gap-4">
            <slot name="attachments" />
            <slot name="context" />
            <div class="relative">
              <span
                v-if="rotationActive && liftText === null"
                aria-hidden="true"
                class="pointer-events-none absolute inset-x-0 top-0 flex items-center gap-4 text-body-medium text-text-tertiary"
              >
                <span class="grid min-w-0 flex-1 overflow-hidden">
                  <Transition name="value-roll">
                    <span :key="rotatingText" class="col-start-1 row-start-1 truncate">{{
                      rotatingText
                    }}</span>
                  </Transition>
                </span>
                <AppKbd v-if="showTabHint" class="shrink-0">{{ t('chat.composer.tabKey') }}</AppKbd>
              </span>
              <span
                v-if="liftText !== null && modelValue === ''"
                aria-hidden="true"
                class="prompt-lift pointer-events-none absolute inset-x-0 top-0 break-words whitespace-pre-wrap text-body-medium text-text-primary"
                @animationend="liftText = null"
                >{{ liftText }}</span
              >
              <textarea
                ref="textareaRef"
                :value="modelValue"
                :rows="minRows"
                :placeholder="hasRotatingPlaceholders ? undefined : resolvedPlaceholder"
                :disabled="disabled"
                :class="[
                  FIELD_TEXT,
                  'block w-full resize-none transition-[height] duration-slow ease-spring motion-reduce:transition-none',
                  accepting ? 'prompt-accept' : '',
                ]"
                @input="onInput"
                @keydown="onKeydown"
                @focus="onFocus"
                @blur="onBlur"
                @paste="onPaste"
                @animationend="onTextareaAnimationEnd"
              />
            </div>
          </div>
          <div
            v-if="acceptsDrop && !isBlocked"
            aria-hidden="true"
            :class="[
              'prompt-drop pointer-events-none absolute inset-0 grid place-items-center rounded-[inherit] bg-halo-blue text-body-sm-emphasized text-accent-blue inset-ring-2 inset-ring-accent-blue',
              'transition-opacity duration-fast ease-standard',
              dragging ? 'opacity-100' : 'opacity-0',
            ]"
          >
            {{ t('chat.composer.dropToAttach') }}
          </div>
        </div>
      </div>
      <div v-if="!isBlocked" class="flex shrink-0 items-center gap-4">
        <slot name="tools" />
        <span
          class="relative inline-grid shrink-0"
          :class="nudge.shaking.value ? 'motion-safe:animate-shake' : ''"
          @animationend="nudge.onAnimationEnd"
        >
          <button
            v-pointer-light
            :type="faceSubmits(face) ? 'submit' : 'button'"
            :disabled="face === 'idle' || face === 'sending'"
            :aria-label="sendLabel"
            :data-face="face"
            :data-pop="popping"
            :class="[SEND_BASE, FACE_SURFACE[face]]"
            @click="onSendClick"
            @animationend.self="onSendAnimationEnd"
          >
            <Transition name="prompt-glyph">
              <span :key="glyph" class="col-start-1 row-start-1 inline-flex">
                <span v-if="glyph === 'arrow'" class="prompt-arrow inline-flex">
                  <ArrowUpIcon />
                </span>
                <AppParticleRing v-else-if="glyph === 'mark'" variant="mark" />
                <StopIcon v-else-if="glyph === 'stop'" />
                <RefreshIcon v-else />
              </span>
            </Transition>
          </button>
          <svg
            :class="[
              'prompt-ring pointer-events-none absolute -inset-1 text-accent-blue',
              face === 'stopping' ? 'motion-safe:animate-spin' : '',
            ]"
            :data-shown="face === 'stopping'"
            viewBox="0 0 40 40"
            aria-hidden="true"
          >
            <circle
              cx="20"
              cy="20"
              r="18"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-dasharray="28 100"
            />
          </svg>
        </span>
        <span v-if="face === 'sending'" role="status" class="sr-only">{{
          t('chat.agentPulse')
        }}</span>
      </div>
    </div>
    <p v-if="keyHint && !isBlocked" class="mt-4 text-caption text-text-tertiary hover-none:hidden">
      {{ t('chat.composer.keyHint') }}
    </p>
  </form>
</template>

<style>
.prompt-field {
  border-radius: var(--radius-8);
  transition:
    border-radius var(--transition-duration-slower) var(--ease-spring),
    box-shadow var(--transition-duration-moderate) var(--ease-spring),
    scale var(--transition-duration-slow) var(--ease-spring);
}
.prompt-field[data-grown='true'] {
  border-radius: var(--radius-composer-grown);
}
.prompt-drop {
  corner-shape: inherit;
}
.prompt-dragging .prompt-field {
  scale: 1.01;
}

.prompt-lift {
  animation: prompt-lift var(--transition-duration-base) var(--ease-soft-exit) both;
}
@keyframes prompt-lift {
  to {
    opacity: 0;
    translate: 0 calc(var(--spacing-4) * -1);
  }
}

.prompt-accept {
  animation: prompt-accept var(--transition-duration-slow) var(--ease-standard);
}
@keyframes prompt-accept {
  from {
    color: var(--color-text-tertiary);
  }
}

.prompt-send[data-pop='true'] {
  animation: prompt-send-pop var(--transition-duration-slower) var(--ease-spring-bouncy);
}
@keyframes prompt-send-pop {
  from {
    scale: 0.82;
  }
}

.prompt-arrow {
  transition: translate var(--transition-duration-slow) var(--ease-spring-bouncy);
}
@media (hover: hover) {
  .prompt-send[data-face='ready']:hover .prompt-arrow {
    translate: 0 calc(var(--spacing-1) * -1);
  }
}

.prompt-glyph-enter-active {
  transition:
    opacity var(--transition-duration-slow) var(--ease-spring),
    scale var(--transition-duration-slow) var(--ease-spring),
    rotate var(--transition-duration-slow) var(--ease-spring);
}
.prompt-glyph-leave-active {
  transition:
    opacity var(--transition-duration-fast) var(--ease-soft-exit),
    scale var(--transition-duration-fast) var(--ease-soft-exit),
    rotate var(--transition-duration-fast) var(--ease-soft-exit);
}
.prompt-glyph-enter-from {
  opacity: 0;
  scale: 0.5;
  rotate: 45deg;
}
.prompt-glyph-leave-to {
  opacity: 0;
  scale: 0.5;
  rotate: -45deg;
}

.prompt-ring {
  opacity: 0;
  transition: opacity var(--transition-duration-base) var(--ease-standard);
}
.prompt-ring[data-shown='true'] {
  opacity: 1;
}

.prompt-plus svg {
  transition: rotate var(--transition-duration-slower) var(--ease-spring-bouncy);
}
@media (hover: hover) {
  .prompt-plus:hover:not(:disabled) svg {
    rotate: 90deg;
  }
}

@media (prefers-reduced-motion: reduce) {
  .prompt-field {
    transition: box-shadow var(--transition-duration-base) var(--ease-standard);
  }
  .prompt-dragging .prompt-field {
    scale: none;
  }
  .prompt-lift,
  .prompt-send[data-pop='true'] {
    animation: none;
  }
  .prompt-glyph-enter-from,
  .prompt-glyph-leave-to {
    scale: none;
    rotate: none;
  }
  .prompt-send[data-face='ready']:hover .prompt-arrow,
  .prompt-plus:hover:not(:disabled) svg {
    translate: none;
    rotate: none;
  }
}
</style>
